/**
 * usePoll
 *
 * Runs `fn` immediately and then on a fixed interval, but only while the tab
 * is visible AND the device is online.  Delegates all pause/resume logic to
 * NetworkGateway — no per-call visibilitychange / online / offline wiring.
 *
 * Usage:
 *   usePoll(fetchPresence, 2 * 60 * 1000);  // 2-minute presence refresh
 *
 * The function reference is stabilised via a ref so you can pass an
 * inline arrow without needing useCallback.
 *
 * @param fn          Function to call (may be async — errors are swallowed).
 * @param intervalMs  Interval in milliseconds.
 * @param enabled     Set to false to skip registration entirely (default true).
 */

import { useEffect, useId, useRef } from 'react';
import { NetworkGateway } from '../services/NetworkGateway';

export function usePoll(
  fn: () => void | Promise<void>,
  intervalMs: number,
  enabled = true,
) {
  const fnRef = useRef(fn);
  fnRef.current = fn; // always up to date, no stale closure

  const id = useId(); // stable per component instance

  useEffect(() => {
    if (!enabled) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const run = () => {
      try {
        const result = fnRef.current();
        if (result instanceof Promise) result.catch(() => {});
      } catch {}
    };

    const start = () => {
      if (timer !== null) return; // already running
      run(); // immediate call on activate / mount
      timer = setInterval(run, intervalMs);
    };

    const stop = () => {
      if (timer !== null) { clearInterval(timer); timer = null; }
    };

    const unregister = NetworkGateway.register(id, {
      onActivate: start,
      onDeactivate: stop,
    });

    // Start immediately if the gateway is currently active.
    if (NetworkGateway.isActive) start();

    return () => {
      stop();
      unregister();
    };
  }, [id, intervalMs, enabled]);
}
